import { ChevronDownIcon } from "@heroicons/react/solid";
import { isEmpty, without } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import CheckIcon from "../../../components/icons/CheckIcon";
import { useDropdownMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import classNames from "../../../utils/classNames";
import { Entity } from "../../../utils/constants";
import TableHeaderCell from "./TableHeaderCell";
import TablePagination from "./TablePagination";
import TableRowCell from "./TableRowCell";
import useDataTable, { Property } from "./useDataTable";
import usePagination, { PaginationKey } from "./usePagination";

export interface SelectionAction<T> {
  title: string;
  callback: (
    selectedItems: T[],
    setSelectedItems: React.Dispatch<React.SetStateAction<T[]>>
  ) => void;
}

interface Props<T> {
  paginationKey: PaginationKey;
  entity: Entity;
  items: T[];
  properties: { [key: string]: Property<T> };
  selectionActions?: SelectionAction<T>[];
}

function EntityDataTable<T>({
  paginationKey,
  entity,
  items,
  properties,
  selectionActions = []
}: React.PropsWithChildren<Props<T>>) {
  const { search: queryParamsString } = useLocation();
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const { showDropdownMenu } = useDropdownMenu();

  const { startIndex, endIndex } = usePagination(
    useCallback(
      (state) =>
        state.getPaginationInfo(paginationKey, items.length, queryParamsString),
      [items.length, queryParamsString, paginationKey]
    )
  );

  const filteredProperties = useDataTable(
    useCallback(
      (state) =>
        state.orderedProperties[entity]
          .filter((key) => state.visibleProperties[entity].includes(key))
          .map((key) => {
            const property = properties[key];
            if (!property) {
              console.log({ entity, key, property });
            }
            return property;
          }),
      [entity, properties]
    )
  );

  const itemsToDisplay = useMemo(
    () => items.slice(startIndex, endIndex),
    [endIndex, items, startIndex]
  );

  const areAllItemsSelected = useMemo(
    () => selectedItems.length > 0 && items.length === selectedItems.length,
    [items.length, selectedItems.length]
  );

  const [mainSelectionAction, ...otherSelectionActions] = useMemo(
    () => selectionActions,
    [selectionActions]
  );

  const handleSelectAll = useCallback(() => {
    setSelectedItems(
      selectedItems.length >= itemsToDisplay.length ? [] : itemsToDisplay
    );
  }, [itemsToDisplay, selectedItems.length]);

  useEffect(() => {
    const table = document.getElementById(`tableAction-${entity}`);
    const selectionHeader = document.getElementById(`selectActions-${entity}`);

    if (!table || !selectionHeader) return;

    selectionHeader.style.marginTop = `${table.scrollTop}px`;

    table?.addEventListener("scroll", (e) => {
      const { scrollTop } = e.target as HTMLTableElement;

      selectionHeader.style.marginTop = `${scrollTop}px`;
    });
  }, [handleSelectAll]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div
        id={`tableAction-${entity}`}
        className="align-middle inline-block min-w-full overflow-x-auto max-w-full border-t border-gray-200 dark:border-gray-500"
      >
        <table className="min-w-full relative">
          <thead className="h-12">
            <tr className="relative border-b border-gray-200 dark:border-gray-500">
              {!isEmpty(selectionActions) && (
                <th
                  style={{ zIndex: 1 }}
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-gray-50 dark:bg-gray-700"
                >
                  <div className="flex items-center justify-start">
                    {selectedItems.length > 0 &&
                      itemsToDisplay.length > 0 &&
                      selectedItems.length !== itemsToDisplay.length && (
                        <div
                          onClick={() => handleSelectAll()}
                          className="absolute flex cursor-pointer items-center justify-center self-center text-center h-4 w-4 text-gray-500 bg-hotspotty-400 dark:text-gray-300 border border-gray-500 rounded checked:border-hotspotty-400"
                        >
                          <span className="font-black text-white">-</span>
                        </div>
                      )}

                    <input
                      onChange={() => handleSelectAll()}
                      checked={
                        (selectedItems.length > 0 &&
                          itemsToDisplay.length > 0 &&
                          selectedItems.length === itemsToDisplay.length) ||
                        areAllItemsSelected ||
                        (items.length > 0 &&
                          items.length === selectedItems.length)
                      }
                      disabled={isEmpty(itemsToDisplay)}
                      type="checkbox"
                      className="text-white flex transition duration-150 ease-in-out sm:text-sm sm:leading-5 focus:outline-none h-4 w-4 self-center cursor-pointer checked:bg-hotspotty-400 appearance-none border border-gray-500 rounded checked:border-gray-500"
                    />
                    <div
                      className="absolute text-white cursor-pointer"
                      onClick={() => handleSelectAll()}
                    >
                      {items.length > 0 &&
                        itemsToDisplay.length === selectedItems.length && (
                          <CheckIcon className="h-4 w-4" />
                        )}
                    </div>
                  </div>
                </th>
              )}

              {filteredProperties.map((property) => (
                <TableHeaderCell
                  key={`header-column-${property.key}`}
                  entity={entity}
                  property={property}
                  className={classNames(
                    !isEmpty(selectionActions) &&
                      !isEmpty(selectedItems) &&
                      "opacity-0"
                  )}
                />
              ))}

              {!isEmpty(selectionActions) && !isEmpty(selectedItems) && (
                <th
                  id={`selectActions-${entity}`}
                  style={{ zIndex: 1, width: "calc(100% - 3rem)" }}
                  className="absolute w-full ml-12 items-center text-gray-500 dark:bg-gray-700 dark:text-gray-300 left-0 top-0 h-full"
                >
                  <div className="flex items-center h-full">
                    <p className="mr-4 text-sm font-normal text-gray-700 dark:text-gray-50">
                      {selectedItems.length} selected
                    </p>

                    {mainSelectionAction && (
                      <div className="flex items-center">
                        <button
                          type="button"
                          className="relative inline-flex items-center h-8 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-hotspotty-400"
                        >
                          <span
                            onClick={() =>
                              mainSelectionAction.callback(
                                selectedItems,
                                setSelectedItems
                              )
                            }
                            className="px-2 text-xs md:text-sm"
                          >
                            {mainSelectionAction.title}
                          </span>
                          {!isEmpty(otherSelectionActions) && (
                            <div
                              onClick={(evt) => {
                                evt?.stopPropagation();

                                showDropdownMenu(
                                  otherSelectionActions.map(
                                    ({ title, callback }) => ({
                                      text: title,
                                      onClick: () =>
                                        callback(
                                          selectedItems,
                                          setSelectedItems
                                        )
                                    })
                                  ),
                                  evt
                                );
                              }}
                              className="flex items-center px-1 border-l h-full border-gray-300 dark:border-gray-700"
                            >
                              <ChevronDownIcon
                                className="w-5 text-white"
                                aria-hidden="true"
                              />
                            </div>
                          )}
                        </button>
                      </div>
                    )}

                    <div className="m-4 text-xs font-normal cursor-pointer md:text-sm">
                      <p
                        className="text-xs font-normal cursor-pointer hover:underline md:text-sm text-hotspotty-400 hover:text-hotspotty-500 dark:text-hotspotty-200 dark:hover:text-white"
                        onClick={() =>
                          areAllItemsSelected
                            ? setSelectedItems([])
                            : setSelectedItems(items)
                        }
                      >
                        {areAllItemsSelected
                          ? "Clear selection"
                          : `Select all ${items.length} items`}
                      </p>
                    </div>
                  </div>
                </th>
              )}
            </tr>
          </thead>

          <tbody className="text-gray-500 divide-y divide-gray-100 dark:divide-gray-500 dark:text-gray-300">
            {isEmpty(items) ? (
              <tr>
                <td className="px-6 py-3" colSpan={100}>
                  No results to show...
                </td>
              </tr>
            ) : (
              itemsToDisplay.map((item: T, index) => (
                <tr
                  key={index}
                  className={classNames(
                    selectedItems.includes(item)
                      ? "bg-gray-100 dark:bg-gray-800"
                      : "dark:bg-gray-900",
                    "dark:hover:bg-gray-800 hover:bg-gray-50"
                  )}
                >
                  {!isEmpty(selectionActions) && (
                    <TableRowCell
                      key={`row-column-checkbox`}
                      entity={entity}
                      isFirst={true}
                    >
                      <input
                        onChange={() =>
                          setSelectedItems((prevState) =>
                            prevState.includes(item)
                              ? without(prevState, item)
                              : prevState.concat(item)
                          )
                        }
                        checked={selectedItems.includes(item)}
                        type="checkbox"
                        className="items-center block transition duration-150 ease-in-out sm:text-sm sm:leading-5 focus:outline-none h-4 w-4 self-center cursor-pointer checked:bg-hotspotty-400 appearance-none border border-gray-500 dark:border-gray-500 rounded checked:border-gray-500"
                      />
                      <div
                        className="absolute text-white -mt-4 cursor-pointer"
                        onClick={() =>
                          setSelectedItems((prevState) =>
                            prevState.includes(item)
                              ? without(prevState, item)
                              : prevState.concat(item)
                          )
                        }
                      >
                        {selectedItems.includes(item) && (
                          <CheckIcon className="h-4 w-4" />
                        )}
                      </div>
                    </TableRowCell>
                  )}

                  {filteredProperties.map((property, index) => (
                    <TableRowCell
                      key={`row-${index}-column-${property.key}`}
                      entity={entity}
                      property={property}
                      isFirst={isEmpty(selectionActions) ? index === 0 : false}
                    >
                      {property.renderContent(item)}
                    </TableRowCell>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        paginationKey={paginationKey}
        itemsCount={items.length}
      />
    </>
  );
}

export default EntityDataTable;
